import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../api/axiosConfig';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/verificarOlvidoPassword', { email });

      if (response.data.requiresTwoFactor) {
        setStep('2fa');
      } else {
        await handleSendEmailCode();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al verificar el usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailCode = async () => {
    setLoading(true);
    setError('');

    try {
      await api.post('/enviarCodigoRestablecimiento', { email });
      setEmailSent(true);
      setStep('emailCode');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar el código');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/verificarOlvidoPassword2FA', { email, twoFactorCode });
      navigate(`/reset-password?token=${response.data.resetToken}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Código inválido. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/verificarCodigoRestablecimiento', { email, codigo: emailCode });
      navigate(`/reset-password?token=${response.data.resetToken}`);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Código inválido. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = step === 'email' ? 0 : step === '2fa' ? 1 : 1;

  return (
    <div className="forgot-container">
      <div className="forgot-card">
        <div className="step-indicator">
          <div className={`step-dot ${stepIndex >= 0 ? 'done' : ''}`}></div>
          <div className={`step-dot ${stepIndex >= 1 ? 'active' : ''}`}></div>
        </div>

        {step === 'email' && (
          <>
            <h2 className="forgot-title">Recuperar contraseña</h2>
            <p className="forgot-subtitle">Ingresá tu email para verificar tu identidad</p>

            {error && <div className="forgot-error">{error}</div>}

            <form onSubmit={handleCheckEmail}>
              <input
                type="email"
                className="forgot-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
                disabled={loading}
              />
              <button type="submit" className="forgot-btn" disabled={loading || !email}>
                {loading ? 'Verificando...' : 'Continuar'}
              </button>
            </form>
          </>
        )}

        {step === '2fa' && (
          <>
            <h2 className="forgot-title">Verificación de dos pasos</h2>
            <p className="forgot-subtitle">Ingresá el código de 6 dígitos de tu aplicación de autenticación</p>

            {error && <div className="forgot-error">{error}</div>}

            <form onSubmit={handleVerify2FA}>
              <input
                type="text"
                className="forgot-input forgot-input-lg"
                value={twoFactorCode}
                onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                disabled={loading}
              />
              <button type="submit" className="forgot-btn" disabled={loading || twoFactorCode.length !== 6}>
                {loading ? 'Verificando...' : 'Verificar código'}
              </button>
              <button type="button" className="forgot-btn-outline" onClick={() => { setStep('email'); setTwoFactorCode(''); setError(''); }} disabled={loading}>
                Volver
              </button>
            </form>
          </>
        )}

        {step === 'emailCode' && (
          <>
            <h2 className="forgot-title">Código de verificación</h2>
            <p className="forgot-subtitle">
              {emailSent
                ? `Te enviamos un código de 6 dígitos a ${email}`
                : 'No se pudo enviar el código. Intentá de nuevo.'}
            </p>

            {error && <div className="forgot-error">{error}</div>}

            <form onSubmit={handleVerifyEmailCode}>
              <input
                type="text"
                className="forgot-input forgot-input-lg"
                value={emailCode}
                onChange={e => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                disabled={loading}
              />
              <button type="submit" className="forgot-btn" disabled={loading || emailCode.length !== 6}>
                {loading ? 'Verificando...' : 'Verificar código'}
              </button>
              <button type="button" className="forgot-btn-outline" onClick={() => { setLoading(true); handleSendEmailCode(); }} disabled={loading}>
                Reenviar código
              </button>
              <button type="button" className="forgot-btn-outline" onClick={() => { setStep('email'); setEmailCode(''); setError(''); setEmailSent(false); }} disabled={loading}>
                Volver
              </button>
            </form>
          </>
        )}

        <Link to="/login" className="forgot-link">
          <i className="bi bi-arrow-left" style={{ marginRight: 4 }}></i>Volver al inicio de sesión
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
